import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as dropboxService from '@app/services/dropbox';
import { setRecentActivity } from '@app/store/reducers/dropbox';

/**
 * Custom hook for recent activity tracking
 */
export const useRecentActivity = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Fetch recent activity
  const fetchRecentActivity = useCallback(
    async (params?: { limit?: number; entity_type?: 'file' | 'folder' }) => {
      try {
        setLoading(true);
        const response = await dropboxService.getRecentActivity(params);

        if (response?.success) {
          dispatch(setRecentActivity(response.data));
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to fetch recent activity';
          toast.error(errorMsg);
          return [];
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while fetching recent activity';
        toast.error(errorMsg);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  // Track file access
  const trackFileAccess = useCallback(async (fileId: string, action: string = 'opened') => {
    try {
      await dropboxService.trackFileAccess(fileId, action);
    } catch (error) {
      // Fail silently for tracking
    }
  }, []);

  // Track folder access
  const trackFolderAccess = useCallback(async (folderId: string, action: string = 'opened') => {
    try {
      await dropboxService.trackFolderAccess(folderId, action);
    } catch (error) {
      // Fail silently for tracking
    }
  }, []);

  // Clear recent activity
  const clearActivity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dropboxService.clearRecentActivity();

      if (response?.success) {
        dispatch(setRecentActivity([]));
        toast.success('Recent activity cleared');
        return true;
      } else {
        const errorMsg = response?.message || 'Failed to clear recent activity';
        toast.error(errorMsg);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'An error occurred while clearing recent activity';
      toast.error(errorMsg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  return {
    loading,
    fetchRecentActivity,
    trackFileAccess,
    trackFolderAccess,
    clearActivity,
  };
};
